import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import { useLocation } from 'react-router-dom';
import { Joyride, STATUS, type EventData, type Step } from 'react-joyride';
import { useSessionStore, type SessionUser } from '@/stores/session';
import { markTourSeen } from '@/features/auth/api';
import { ONBOARDING_STEPS, TOUR_SECTIONS, type TourSection } from '@/config/tours';

// Resuelve un `pathname` real (ej: `/products/SKU-001`) contra las keys
// del registro (`/products/:sku`). Match segmentado (sin RegExp dinámico
// para evitar warnings de ReDoS): split por `/`, comparar longitud y
// luego cada segmento literal — los `:foo` matchean cualquier string.
export function matchRouteKey(pathname: string, keys: string[]): string | null {
  if (keys.includes(pathname)) return pathname;
  const pathSegments = pathname.split('/');
  for (const key of keys) {
    if (!key.includes(':')) continue;
    const keySegments = key.split('/');
    if (keySegments.length !== pathSegments.length) continue;
    const matches = keySegments.every((seg, i) => {
      if (seg.startsWith(':')) return pathSegments[i].length > 0;
      return seg === pathSegments[i];
    });
    if (matches) return key;
  }
  return null;
}

// Provider del tour onboarding + ayuda on-demand.
//
// - Auto-dispara `ONBOARDING_STEPS` cuando el user está autenticado y aún
//   no marcó como visto el tour de raíz (`tourState.seen['/']`), salvo que
//   `tourState.neverShow === true` (mute global del usuario).
// - Expone `useTour()` con `runOnboarding()` (manual), `runSection()`
//   (ayuda puntual sin persistir) y `runFullTour()` (recorre todo y marca
//   la ruta como vista al terminar).
// - Source of truth de "qué tours vio el user": `users.tour_state` en el
//   backend. Marcado vía `PATCH /auth/me/tour-state/seen` cuando el user
//   completa o skippea un tour persistible.
// - Lib: react-joyride 3.x. Styles overrideados a design tokens del proyecto.

// Key del onboarding global en `tourState.seen`. Coincide con el path raíz —
// el menú "?" del dashboard también lo expone.
const ONBOARDING_KEY = '/';

// Delay tras detectar primer login: dejamos que el shell + dashboard
// renderen para que los `data-tour` targets existan en el DOM.
const ONBOARDING_BOOT_DELAY_MS = 600;

// Limpieza one-shot del flag legacy. Antes de tener `tour_state` en backend,
// persistíamos el onboarding visto en `localStorage`. Lo borramos en el
// próximo mount para no dejar basura — el backend ahora es source of truth.
const LEGACY_ONBOARDING_KEY = 'cef:tour:onboarding-seen';

function cleanupLegacyOnboardingFlag(): void {
  try {
    window.localStorage.removeItem(LEGACY_ONBOARDING_KEY);
  } catch {
    /* localStorage bloqueado: ignoramos */
  }
}

function hasSeen(user: SessionUser | null, key: string): boolean {
  return user?.tourState?.seen?.[key] === true;
}

function isMuted(user: SessionUser | null): boolean {
  return user?.tourState?.neverShow === true;
}

interface TourContextValue {
  // Dispara el onboarding global. Ignora el flag de "visto" — útil para el
  // ítem "Repetir tour" del menú de ayuda.
  runOnboarding: () => void;
  // Dispara una sección puntual de la ruta indicada. NO marca como visto
  // (es ayuda contextual). Si no existe, no-op.
  runSection: (routePath: string, sectionId: string) => void;
  // Tour completo: concatena los steps de TODAS las secciones registradas
  // para la ruta y los corre en orden. Al terminar marca la ruta como
  // vista en el backend.
  runFullTour: (routePath: string) => void;
  // Devuelve secciones registradas para esa ruta (helper para el menú).
  sectionsForRoute: (routePath: string) => TourSection[];
  isRunning: boolean;
}

const TourContext = createContext<TourContextValue | null>(null);

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) {
    throw new Error('useTour debe usarse dentro de <TourProvider>.');
  }
  return ctx;
}

// Styles del Joyride v3 — tematización del tooltip/botones para que respete
// los design tokens del proyecto (dark mode incluido).
const JOYRIDE_STYLES = {
  tooltipContainer: {
    textAlign: 'left' as const,
  },
  tooltipTitle: {
    fontSize: 15,
    fontWeight: 600,
  },
  tooltipContent: {
    padding: '8px 0',
    fontSize: 13,
    lineHeight: 1.4,
  },
  // Botón lleno: `primary-solid`, como `Button` (en oscuro el azul vivo con
  // texto blanco no llega a AA).
  buttonNext: {
    backgroundColor: 'var(--color-primary-solid)',
    color: 'var(--color-primary-solid-fg)',
    borderRadius: 6,
    fontSize: 13,
  },
  buttonBack: {
    color: 'var(--color-text-muted)',
    fontSize: 13,
  },
  buttonSkip: {
    color: 'var(--color-text-muted)',
    fontSize: 13,
  },
};

// Joyride v3: `options` es prop SEPARADA (no anidada en styles). Setea
// defaults globales para todos los steps. `buttons` incluye 'skip' para que
// el botón Saltar aparezca; sin esa entry no se renderiza aunque LOCALE.skip
// esté definido.
const JOYRIDE_OPTIONS = {
  primaryColor: 'var(--color-primary)',
  backgroundColor: 'var(--color-surface)',
  textColor: 'var(--color-text)',
  arrowColor: 'var(--color-surface)',
  overlayColor: 'rgba(0, 0, 0, 0.55)',
  zIndex: 10_000,
  showProgress: true,
  buttons: ['back', 'skip', 'primary'] as ('back' | 'skip' | 'primary' | 'close')[],
};

const LOCALE = {
  back: 'Atrás',
  close: 'Cerrar',
  last: 'Finalizar',
  next: 'Siguiente',
  nextWithProgress: 'Siguiente ({current} de {total})',
  open: 'Abrir',
  skip: 'Saltar',
};

// `kind` distingue el comportamiento al cerrar:
// - 'onboarding': marcar `/` como visto.
// - 'fullTour':   marcar `routeKey` como visto.
// - 'section':    no marcar nada (ayuda puntual).
interface RunState {
  steps: Step[];
  run: boolean;
  kind: 'onboarding' | 'fullTour' | 'section' | null;
  routeKey?: string;
}

const INITIAL_STATE: RunState = { steps: [], run: false, kind: null };

// Joyride por defecto muestra un "beacon" (círculo pulsante) antes del
// popover — el user tiene que clickear el beacon para ver el tooltip. Para
// onboarding/full tour queremos que el popover aparezca directo. Aplicamos
// `skipBeacon: true` a todos los steps acá en vez de en cada config —
// menos repetición y la regla queda en un solo lugar. (En Joyride v2 esta
// prop se llamaba `disableBeacon`; v3 la renombró a `skipBeacon`.)
function withBeaconDisabled(steps: Step[]): Step[] {
  return steps.map((s) => ({ ...s, skipBeacon: true }));
}

// Persiste "tour visto" en el backend de forma optimista: actualiza el
// store local primero, luego PATCH. Si el PATCH falla (red, 5xx), revierte
// el cambio local — así el usuario vuelve a ver el tour la próxima vez,
// que es el lado seguro del error.
async function persistSeen(routeKey: string): Promise<void> {
  const { updateTourState, user } = useSessionStore.getState();
  const prevSeen = user?.tourState?.seen ?? {};
  if (prevSeen[routeKey] === true) return; // ya marcado, no-op
  updateTourState({ seen: { ...prevSeen, [routeKey]: true } });
  try {
    await markTourSeen(routeKey);
  } catch {
    updateTourState({ seen: prevSeen });
  }
}

export const TourProvider = ({ children }: PropsWithChildren) => {
  const [state, setState] = useState<RunState>(INITIAL_STATE);

  // Limpieza one-shot del localStorage legacy. Se ejecuta una vez por mount;
  // como el provider vive en `Providers.tsx` (root del árbol), basta.
  useEffect(() => {
    cleanupLegacyOnboardingFlag();
  }, []);

  const runOnboarding = useCallback(() => {
    setState({
      steps: withBeaconDisabled(ONBOARDING_STEPS),
      run: true,
      kind: 'onboarding',
      routeKey: ONBOARDING_KEY,
    });
  }, []);

  const runSection = useCallback((routePath: string, sectionId: string) => {
    const key = matchRouteKey(routePath, Object.keys(TOUR_SECTIONS)) ?? routePath;
    const sections = TOUR_SECTIONS[key] ?? [];
    const found = sections.find((s) => s.id === sectionId);
    if (!found) return;
    setState({
      steps: withBeaconDisabled(found.steps),
      run: true,
      kind: 'section',
    });
  }, []);

  const runFullTour = useCallback((routePath: string) => {
    const key = matchRouteKey(routePath, Object.keys(TOUR_SECTIONS)) ?? routePath;
    const sections = TOUR_SECTIONS[key] ?? [];
    if (sections.length === 0) return;
    const allSteps = sections.flatMap((s) => s.steps);
    setState({
      steps: withBeaconDisabled(allSteps),
      run: true,
      kind: 'fullTour',
      routeKey: key,
    });
  }, []);

  const sectionsForRoute = useCallback((routePath: string): TourSection[] => {
    const key = matchRouteKey(routePath, Object.keys(TOUR_SECTIONS)) ?? routePath;
    return TOUR_SECTIONS[key] ?? [];
  }, []);

  // Callback de Joyride: cuando finaliza/skipea, marcamos como visto en el
  // backend según el `kind` del run actual y apagamos.
  function handleEvent(data: EventData): void {
    const { status } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];
    if (!finishedStatuses.includes(status)) return;
    if (state.kind === 'onboarding' || state.kind === 'fullTour') {
      if (state.routeKey) {
        void persistSeen(state.routeKey);
      }
    }
    setState(INITIAL_STATE);
  }

  const value = useMemo<TourContextValue>(
    () => ({
      runOnboarding,
      runSection,
      runFullTour,
      sectionsForRoute,
      isRunning: state.run,
    }),
    [runOnboarding, runSection, runFullTour, sectionsForRoute, state.run],
  );

  return (
    <TourContext.Provider value={value}>
      <Joyride
        steps={state.steps}
        run={state.run}
        onEvent={handleEvent}
        continuous
        scrollToFirstStep
        styles={JOYRIDE_STYLES}
        options={JOYRIDE_OPTIONS}
        locale={LOCALE}
      />
      {children}
    </TourContext.Provider>
  );
};

// Componente null que dispara tours automáticamente según la ruta actual.
// VIVE DENTRO del Router (necesita `useLocation`). El TourProvider en cambio
// vive en `Providers.tsx`, envolviendo TODO el árbol — ahí no se puede usar
// useLocation porque el RouterProvider aún no se montó.
//
// Reglas de auto-trigger:
// - no auth / no user / silenciado / tour ya corriendo → skip.
// - onboarding (key `/`) tiene prioridad sobre el tour de la ruta actual.
// - sino, si la ruta tiene tour registrado y no fue visto → full tour.
// Delay = ONBOARDING_BOOT_DELAY_MS para que los `data-tour` targets monten
// tras el render inicial / navegación.
export const TourAutoTrigger = (): null => {
  const user = useSessionStore((s) => s.user);
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated);
  const location = useLocation();
  const { runOnboarding, runFullTour, isRunning } = useTour();

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    // Gate por `tourState` DEFINIDO: si aún no llegó `/auth/me` que hidrata
    // el estado desde backend, `user.tourState === undefined` — no sabemos
    // si el user ya vio el tour. Esperar evita disparar por default y luego
    // "apagar" cuando llega la respuesta (flicker de tour no deseado).
    if (user.tourState === undefined) return;
    if (isMuted(user)) return;
    if (isRunning) return;

    let action: (() => void) | null = null;
    if (!hasSeen(user, ONBOARDING_KEY)) {
      action = runOnboarding;
    } else {
      const key = matchRouteKey(location.pathname, Object.keys(TOUR_SECTIONS));
      if (key && (TOUR_SECTIONS[key]?.length ?? 0) > 0 && !hasSeen(user, key)) {
        action = () => runFullTour(location.pathname);
      }
    }

    if (!action) return;
    const t = setTimeout(action, ONBOARDING_BOOT_DELAY_MS);
    return () => clearTimeout(t);
  }, [isAuthenticated, user, location.pathname, isRunning, runOnboarding, runFullTour]);

  return null;
};
